import { formatDate } from './util';

// Bond detail page helpers (PB-2339). The backend returns dates as
// "yyyy-MM-dd", prices as fractions of par, and rates/yields in percent.

const SECTORS = {
  BNK: 'Banks',
  FIN: 'Financials',
  GOV: 'Government',
  IFT: 'Information technology',
  HLC: 'Healthcare',
  IND: 'Industrials',
  CDI: 'Consumer discretionary',
  CST: 'Consumer staples',
  CMU: 'Communication services',
  QGO: 'Quasi-government',
};

const FREQUENCIES = {
  0: 'Zero coupon',
  1: 'Annual',
  2: 'Semi-annual',
  4: 'Quarterly',
  12: 'Monthly',
};

const SOURCES = {
  CUSTODIAN: 'Custodian',
  DBS_FEED: 'DBS feed',
  HOLDINGS_MIGRATED: 'Migrated holding',
};

export const todayIso = () => {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

export const orDash = (v) => (v === null || v === undefined || v === '' ? '-' : v);

// Parse "yyyy-MM-dd" as a local calendar date; Date.parse would treat it as
// UTC midnight and show the previous day west of UTC.
export const showBondDate = (iso) => {
  if (!iso) return '-';
  const [y, m, d] = iso.split('-').map(Number);
  return formatDate(new Date(y, m - 1, d), 'dd mmm yyyy');
};

export const daysBetween = (fromIso, toIso) => {
  const toUtc = (iso) => {
    const [y, m, d] = iso.split('-').map(Number);
    return Date.UTC(y, m - 1, d);
  };
  return Math.round((toUtc(toIso) - toUtc(fromIso)) / 86400000);
};

export const showParPrice = (p) => (p == null ? '-' : (p * 100).toFixed(3));

export const showRate = (r, dp = 3) => (r == null ? '-' : `${Number(r).toFixed(dp)}%`);

export const showBondAmount = (a, ccy) =>
  a == null
    ? '-'
    : `${ccy} ${Number(a).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;

export const sectorName = (code) => SECTORS[code] || orDash(code);

export const frequencyName = (f) => (f == null ? null : FREQUENCIES[f] || `${f}× a year`);

// Label for a quote row or the selected indicative price.
export const priceSourceName = (price) => {
  if (price.source !== 'CBONDS') return SOURCES[price.source] || price.source;
  // Cbonds quotes stored before per-venue listings existed have no venue.
  const venue = price.venueName;
  if (!venue) return 'Cbonds';
  return venue.startsWith('Cbonds') ? venue : `Cbonds · ${venue}`;
};

// flowType is COUPON / AMORTISATION / REDEMPTION (pms_bond_coupon_schedule).
export const isPrincipalFlow = (f) =>
  f.flowType === 'REDEMPTION' || f.flowType === 'AMORTISATION';

// An empty schedule (flows not fetched yet) or a null master rate on a
// floating-rate note is not evidence of a zero coupon.
export const isZeroCoupon = (bond) => {
  if (bond.couponFrequency === 0) return true;
  const coupons = bond.couponSchedule.filter((f) => f.flowType === 'COUPON');
  if (coupons.length > 0) return coupons.every((f) => f.couponRate === 0);
  return bond.couponRate === 0;
};

// Face amount that coupon amounts are quoted on (vendor nominal price).
export const couponNominal = (bond) =>
  bond.couponSchedule.find((f) => f.nominalPrice != null)?.nominalPrice ?? bond.parValue;

export const bondStatus = (bond, today = todayIso()) => {
  if (!bond.maturityDate) return 'PERPETUAL';
  if (bond.maturityDate < today) return 'MATURED';
  return 'ACTIVE';
};

// Date the coupon turns floating (start of the first floating period).
export const floatingFrom = (bond) => {
  const first = bond.couponSchedule.find((f) => f.isFloating);
  return first ? first.periodStartDate : null;
};

export const nextCoupon = (bond, today = todayIso()) =>
  bond.couponSchedule.find((f) => f.flowType === 'COUPON' && f.paymentDate >= today) || null;

// Cbonds writes no REDEMPTION flows yet (the "redemtion" field is misread),
// so a non-perpetual bond gets a derived redemption-at-par row on maturity.
export const cashflowsWithRedemption = (bond) => {
  const rows = [...bond.couponSchedule];
  if (bond.maturityDate && !rows.some((f) => f.flowType === 'REDEMPTION')) {
    rows.push({
      paymentDate: bond.maturityDate,
      periodStartDate: null,
      flowType: 'REDEMPTION',
      couponRate: null,
      couponAmount: couponNominal(bond),
      redemptionPercentage: 100,
      isEstimated: false,
      isFloating: false,
      derived: true,
    });
  }
  return rows.sort((a, b) => a.paymentDate.localeCompare(b.paymentDate));
};

const INITIALS_SKIP = /^(of|and|the|corp|corporation|inc|plc|sa|co|ltd|limited)$/i;

// Two-letter issuer mark for the avatar, e.g. "Bank of America" -> "BA".
export const issuerInitials = (text) =>
  (text || '')
    .replace(/[^A-Za-z ]/g, ' ')
    .split(' ')
    .filter((w) => w && !INITIALS_SKIP.test(w))
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join('') || '?';

// Share of the bond's life (issue -> maturity) already elapsed, 0-100; null
// for a perpetual.
export const lifeElapsedPercent = (bond, today = todayIso()) => {
  if (!bond.maturityDate || !bond.issueDate) return null;
  const total = Math.max(1, daysBetween(bond.issueDate, bond.maturityDate));
  return Math.min(100, Math.max(0, (daysBetween(bond.issueDate, today) / total) * 100));
};

export const yearsToMaturity = (bond, today = todayIso()) =>
  bond.maturityDate ? daysBetween(today, bond.maturityDate) / 365.25 : null;

// Upcoming coupons, redemptions and call/put dates in date order. A call
// window already open is shown from today.
export const upcomingEvents = (bond, today = todayIso()) => {
  const flows = cashflowsWithRedemption(bond)
    .filter((f) => f.paymentDate >= today)
    .map((f) => ({
      date: f.paymentDate,
      kind: isPrincipalFlow(f) ? 'redemption' : 'coupon',
      flow: f,
    }));
  const options = bond.callPutSchedule
    .filter((c) => (c.untilDate || c.effectiveDate) >= today)
    .map((c) => ({
      date: c.effectiveDate < today ? today : c.effectiveDate,
      kind: 'option',
      option: c,
    }));
  return [...flows, ...options].sort((a, b) => a.date.localeCompare(b.date));
};
